import React, {
  CSSProperties,
  Children,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import { colors } from '@styles/colors';

export type AccordionProps = {
  id?: string;
  label?: string;
  children?: ReactNode;
  header?: ReactNode | (({ isOpen }: { isOpen: boolean }) => ReactNode);
  defaultOpen?: boolean;
  isOpen?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  wrapperStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  onToggle?: (isOpen: boolean) => void;
  onHeaderClick?: () => void;
};

const Accordion: React.FC<AccordionProps> = ({
  id,
  label,
  children,
  header,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  wrapperStyle,
  contentStyle,
  onToggle,
  onHeaderClick,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const [contentHeight, setContentHeight] = useState<number | undefined>(defaultOpen ? undefined : 0);

  const contentRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const accordionId = `accordion-${id}`;
  const headerId = `${accordionId}-header`;
  const contentId = `${accordionId}-content`;

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }
    setContentHeight(isOpen ? contentRef.current.scrollHeight : 0);
  }, [isOpen, children]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    if (controlledIsOpen === undefined) {
      setInternalIsOpen(!isOpen);
    }
    onToggle?.(isOpen);
    onHeaderClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <AccordionWrapper style={wrapperStyle}>
      <AccordionHeaderButton
        id={headerId}
        aria-expanded={isOpen}
        aria-controls={contentId}
        aria-label={ariaLabel || label}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <HeaderContent>
          {typeof header === 'function' ? header({ isOpen }) : header || <Label>{label}</Label>}
        </HeaderContent>
      </AccordionHeaderButton>
      <ContentWrapper contentHeight={contentHeight}>
        <Content ref={contentRef} style={contentStyle}>
          {children}
        </Content>
      </ContentWrapper>
    </AccordionWrapper>
  );
};

export type AccordionGroupProps = {
  children: ReactNode;
  allowMultiple?: boolean;
  defaultOpenIndexes?: number[];
  className?: string;
  onToggle?: (openIndexes: number[]) => void;
};

const AccordionGroup: React.FC<AccordionGroupProps> = ({
  children,
  allowMultiple = false,
  defaultOpenIndexes = [],
  className,
  onToggle,
}) => {
  const [openIndexes, setOpenIndexes] = useState<number[]>(defaultOpenIndexes);

  const handleToggle = (index: number) => {
    let newOpenIndexes: number[];
    if (allowMultiple) {
      newOpenIndexes = openIndexes.includes(index) ? openIndexes.filter((i) => i !== index) : [...openIndexes, index];
    } else {
      newOpenIndexes = openIndexes.includes(index) ? [] : [index];
    }
    setOpenIndexes(newOpenIndexes);
    onToggle?.(newOpenIndexes);
  };

  const accordionChildren = Children.map(children, (child, index) => {
    if (isValidElement(child) && child.type === Accordion) {
      return cloneElement(child as React.ReactElement<AccordionProps>, {
        isOpen: openIndexes.includes(index),
        onToggle: () => handleToggle(index),
        key: index,
      });
    }
    return child;
  });

  return <AccordionGroupWrapper className={className}>{accordionChildren}</AccordionGroupWrapper>;
};

export default Accordion;
export { AccordionGroup };

const AccordionWrapper = styled.div`
  width: 100%;
  background-color: white;
  border-bottom: 1px solid ${colors.gray50};
`;

const AccordionHeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  text-align: left;
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  width: 100%;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentWrapper = styled.div<{ contentHeight: number | undefined }>`
  overflow: hidden;
  transition: height 0.2s ease-in-out;
  height: ${(props) => (props.contentHeight !== undefined ? `${props.contentHeight}px` : 'auto')};
`;

const Content = styled.div`
  padding: 14px 16px;
  font-size: 14px;
  color: ${colors.black};
  background-color: ${colors.gray70};
  white-space: pre-wrap;
`;

const AccordionGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
